<template>
  <SettingsPageLayout>
    <div class="mx-auto max-w-5xl">
      <div class="mb-4 flex justify-between items-center">
        <h1 class="block text-2xl font-bold leading-6 text-gray-900">E-signature Templates</h1>
        <Button as-child>
          <router-link to="/settings/templates/esignature/new">
            New
            <vue-feather type="plus-circle" class="h-4 w-4" />
          </router-link>
        </Button>
      </div>

      <SearchInput v-model="search" placeholder="Search templates" class="mb-4" />

      <HFetchQuery :query="query">
        <template #loading>
          <div class="flex justify-center py-16">
            <div class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
          </div>
        </template>
        <template #default="{ data }">
          <div
            v-if="filterTemplates(data).length"
            class="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200"
          >
            <TemplateRow
              v-for="template of filterTemplates(data)"
              :key="template.id"
              :template="{ type: 'esignature', data: template }"
              @click-delete="
                selectedTemplate = template;
                showTemplateDelete = true;
              "
              @click-edit="router.push(`/settings/templates/esignature/${template.id}/edit`)"
            />
          </div>
          <p v-else class="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500">
            {{ search ? `No templates match "${search}".` : 'There are no e-signature templates yet.' }}
          </p>
        </template>
      </HFetchQuery>
    </div>

    <EsignatureTemplateDelete
      v-if="showTemplateDelete"
      :selected-template-id="selectedTemplate?.id"
      @handle-template-delete-cancel="showTemplateDelete = false"
      @handle-save-settings="
        query.reload();
        showTemplateDelete = false;
      "
    />
  </SettingsPageLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import EsignatureTemplateDelete from './EsignatureTemplateDelete.vue';
import TemplateRow from './TemplateRow.vue';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { useRouter } from 'vue-router';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import { hFetch } from '@/api/hFetch';
import type { EsignatureTemplateEntity } from '@/api/entity_types/EsignatureTemplate';

const router = useRouter();

const search = ref('');
const showTemplateDelete = ref(false);
const selectedTemplate = ref<EsignatureTemplateEntity | null>(null);

const query = useHFetchQuery<EsignatureTemplateEntity[]>(async () => {
  const response = await hFetch<{ records: EsignatureTemplateEntity[] }>({ url: '/api/esignature_templates' });
  return { status: 'success' as const, data: response?.records ?? [] };
});

const filterTemplates = (templates: EsignatureTemplateEntity[]) => {
  const term = search.value.trim().toLowerCase();
  if (!term) return templates;
  return templates.filter(template => template.name.toLowerCase().includes(term));
};
</script>
